import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Input,
  Progress,
  Radio,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ClearOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ColumnsType } from 'antd/es/table';

import {
  AiClipperMetricsFilter,
  AiClipperSourceType,
  AiClipperTimeSeriesGranularity,
  AiClipperUserBreakdownRow,
} from 'api/aiClipperMetrics/types';

import styles from 'pages/AIClipperMetrics/AIClipperMetrics.module.scss';
import KpiCard from 'pages/AIClipperMetrics/KpiCard';
import {
  DateRange,
  GRANULARITY_ALIGNMENT,
  formatBucketLabel,
  formatRatePct,
} from 'pages/AIClipperMetrics/helpers';
import { useClipperMetricsDashboard } from 'pages/AIClipperMetrics/useClipperMetricsDashboard';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SOURCE_TYPES: AiClipperSourceType[] = ['PpvStream'];

const EXTRA_FILTER: AiClipperMetricsFilter = { sourceTypes: SOURCE_TYPES };

const ERROR_MESSAGES = {
  summary: 'Failed to load PPV clip analytics.',
  userBreakdown: 'Failed to load host breakdown.',
  series: 'Failed to load time series.',
};

const CHART_SERIES: { dataKey: 'Generated' | 'Published'; stroke: string }[] = [
  { dataKey: 'Generated', stroke: '#1677ff' },
  { dataKey: 'Published', stroke: '#16a34a' },
];

const hostColumns: ColumnsType<AiClipperUserBreakdownRow> = [
  {
    title: 'Host',
    key: 'name',
    render: (_: unknown, row) => (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Text strong>
          {row.userName || <Text type="secondary">Unknown</Text>}
        </Text>
        {row.userSlug ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.userSlug}
          </Text>
        ) : null}
      </div>
    ),
  },
  {
    title: 'Generated',
    dataIndex: 'candidatesGenerated',
    key: 'candidatesGenerated',
    width: 140,
    align: 'right',
    render: (value: number) => (
      <Text className={styles.numericCell}>{value}</Text>
    ),
  },
  {
    title: 'Published',
    dataIndex: 'systemAccepted',
    key: 'systemAccepted',
    width: 140,
    align: 'right',
    render: (value: number) => (
      <Tag color="green" className={styles.numericCell}>
        {value}
      </Tag>
    ),
  },
  {
    title: 'Pending',
    dataIndex: 'pendingReview',
    key: 'pendingReview',
    width: 140,
    align: 'right',
    render: (value: number) => (
      <Tag color={value ? 'gold' : 'default'} className={styles.numericCell}>
        {value}
      </Tag>
    ),
  },
];

const PpvClipAnalytics: React.FC = () => {
  const {
    athleteInput,
    range,
    setRange,
    granularity,
    setGranularity,
    baseFiltersDirty,
    summary,
    summaryLoading,
    userRows,
    userTotal,
    userTableLoading,
    userPage,
    setUserPage,
    userPageSize,
    setUserPageSize,
    seriesResult,
    seriesLoading,
    error,
    setError,
    handleRefresh,
    handleClear,
    handleAthleteChange,
  } = useClipperMetricsDashboard({
    extraFilter: EXTRA_FILTER,
    errorMessages: ERROR_MESSAGES,
  });

  const publishRate =
    summary.totalGenerated === 0
      ? null
      : (summary.systemAccepted / summary.totalGenerated) * 100;
  const publishBarValue = publishRate ?? 0;

  const chartData = useMemo(
    () =>
      seriesResult.points.map((point) => ({
        label: formatBucketLabel(point.bucketStart, granularity),
        Generated: point.totalGenerated,
        Published: point.systemAccepted,
      })),
    [seriesResult.points, granularity]
  );

  const kpiCards = useMemo(
    () => [
      { title: 'Generated', value: summary.totalGenerated },
      {
        title: 'Published',
        value: summary.systemAccepted,
        variantClass: styles.kpiAccepted,
      },
      {
        title: 'Pending',
        value: summary.notReviewed,
        variantClass: styles.kpiPending,
      },
    ],
    [summary]
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <Title level={4} className={styles.title}>
            PPV Clip Analytics
          </Title>
          <Text type="secondary">
            Generated vs auto-published PPV highlights.
          </Text>
        </div>
        <div className={styles.filterBar}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Host slug or name…"
            value={athleteInput}
            onChange={handleAthleteChange}
            allowClear
            style={{ width: 240 }}
          />
          <RangePicker
            value={range as any}
            onChange={(next) => setRange(next as DateRange)}
            allowClear
          />
          <Space>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
              disabled={!baseFiltersDirty}
            >
              Clear
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={summaryLoading || userTableLoading || seriesLoading}
            >
              Refresh
            </Button>
          </Space>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <div className={styles.kpiRow}>
        {kpiCards.map((card) => (
          <KpiCard
            key={card.title}
            title={card.title}
            value={card.value}
            loading={summaryLoading}
            variantClass={card.variantClass}
          />
        ))}
      </div>

      <div className={styles.approvalCard}>
        <div>
          <div className={styles.approvalLabel}>Publish rate</div>
          <div className={styles.approvalValue}>
            {summaryLoading ? '…' : formatRatePct(publishRate)}
          </div>
        </div>
        <div className={styles.approvalProgress}>
          <Progress
            percent={Number(publishBarValue.toFixed(1))}
            strokeColor="#16a34a"
            trailColor="rgba(0,0,0,0.06)"
            showInfo={false}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {summary.systemAccepted} published of {summary.totalGenerated}{' '}
            generated
            {summary.notReviewed ? ` (${summary.notReviewed} pending)` : ''}
          </Text>
        </div>
      </div>

      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <div>
            <div className={styles.chartTitle}>Generated vs published</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Buckets aligned to {GRANULARITY_ALIGNMENT[granularity]}.
            </Text>
          </div>
          <Radio.Group
            value={granularity}
            onChange={(e) =>
              setGranularity(e.target.value as AiClipperTimeSeriesGranularity)
            }
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="Day">Day</Radio.Button>
            <Radio.Button value="Week">Week</Radio.Button>
            <Radio.Button value="Month">Month</Radio.Button>
            <Radio.Button value="Year">Year</Radio.Button>
          </Radio.Group>
        </div>
        {seriesResult.truncated && (
          <Alert
            type="warning"
            message="Showing the first 260 buckets — narrow the date range or use a coarser granularity to see the full series."
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}
        <div className={styles.chartBody}>
          {seriesLoading ? (
            <div className={styles.chartLoading}>
              <Spin />
            </div>
          ) : chartData.length === 0 ? (
            <div className={styles.chartEmpty}>
              <Text type="secondary">No PPV clips in this range.</Text>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(0,0,0,0.06)"
                />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Legend />
                {CHART_SERIES.map(({ dataKey, stroke }) => (
                  <Line
                    key={dataKey}
                    type="monotone"
                    dataKey={dataKey}
                    stroke={stroke}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <Table<AiClipperUserBreakdownRow>
        rowKey={(r) => r.userId}
        columns={hostColumns}
        dataSource={userRows}
        loading={userTableLoading}
        pagination={{
          current: userPage,
          pageSize: userPageSize,
          total: userTotal,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (newPage, newPageSize) => {
            if (newPageSize !== userPageSize) {
              setUserPageSize(newPageSize);
              setUserPage(1);
            } else {
              setUserPage(newPage);
            }
          },
        }}
      />
    </div>
  );
};

export default PpvClipAnalytics;
